import LegalPage from './LegalPage'

export default function TermsPage() {
  return (
    <LegalPage title="Terms of Service" lastUpdated="April 8, 2026">
      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mb-3">
        1. Agreement to Terms
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        By accessing or using GCodr ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. We may update these terms from time to time, and your continued use constitutes acceptance of any changes.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        2. Service Description
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        GCodr is an AI-powered tool that analyzes engineering drawings (DXF, DWG, STEP, PDF) and generates CNC G-code for milling operations. The Service uses artificial intelligence and large language models to interpret geometry, select tooling, calculate machining parameters, and produce G-code programs.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        3. AI-Generated Content Disclaimer
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        This is the most important section of these terms. Please read it carefully.
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>All G-code produced by GCodr is generated by artificial intelligence and may contain errors, inaccuracies, or omissions.</li>
        <li>Generated G-code has not been verified on physical CNC machines by GCodr.</li>
        <li>GCodr makes no guarantee of the correctness, completeness, safety, or fitness for purpose of any generated G-code.</li>
        <li>AI-generated toolpaths, feeds, speeds, and depths of cut may be inappropriate for your specific material, machine, or tooling configuration.</li>
        <li>Running unverified G-code on CNC machinery can result in machine damage, tool breakage, workpiece destruction, or serious personal injury.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        4. User Responsibilities
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        By using the Service, you acknowledge and agree that you:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>Must review and verify all generated G-code before running it on any CNC machine.</li>
        <li>Must perform dry runs, simulations, or air cuts before cutting material.</li>
        <li>Must ensure proper safety measures are in place, including machine guards, proper workholding, and personal protective equipment (PPE).</li>
        <li>Must verify that feeds, speeds, and depths of cut are appropriate for your specific material, machine, and tooling.</li>
        <li>Are solely responsible for the safe operation of your CNC equipment.</li>
        <li>Must have appropriate training, expertise, and qualifications to operate CNC machinery.</li>
        <li>Understand that GCodr is a tool to assist — not replace — qualified machinists and programmers.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        5. Limitation of Liability
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        To the maximum extent permitted by applicable law:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>GCodr is not liable for any damage to CNC machines, tooling, workpieces, or other property resulting from the use of generated G-code.</li>
        <li>GCodr is not liable for any personal injury arising from the use of generated G-code.</li>
        <li>GCodr is not liable for production losses, business interruption, or any consequential, incidental, or indirect damages.</li>
        <li>The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
        <li>GCodr's total aggregate liability to you for all claims arising from use of the Service shall not exceed the amount you paid to GCodr in the twelve (12) months preceding the claim.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        6. Intellectual Property
      </h2>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>You retain full ownership of your engineering drawings and any G-code generated from them.</li>
        <li>By using the Service, you grant GCodr a limited, non-exclusive license to process your drawings for the purpose of providing the Service.</li>
        <li>GCodr may use anonymized, aggregated data derived from usage patterns to improve the Service. This data will not include your original drawings or identifiable information.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        7. Account Terms
      </h2>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>You must provide accurate and complete information when creating an account.</li>
        <li>You are responsible for maintaining the security of your account credentials.</li>
        <li>You must not share your account with others or allow unauthorized access.</li>
        <li>You must notify us immediately of any unauthorized use of your account.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        8. Acceptable Use
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        You agree not to:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-sm text-slate-400 mb-4">
        <li>Use the Service for any illegal purpose or in violation of any applicable laws.</li>
        <li>Attempt to reverse-engineer, decompile, or extract source code from the Service.</li>
        <li>Upload malicious files, viruses, or content intended to disrupt the Service.</li>
        <li>Circumvent any access restrictions or rate limits.</li>
        <li>Use automated tools to scrape or extract data from the Service.</li>
      </ul>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        9. Service Availability
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        GCodr is currently in early preview. The Service is provided on an "as available" basis. We do not guarantee any specific uptime or availability. Features may change, be added, or be removed at any time. We reserve the right to modify, suspend, or discontinue the Service at our sole discretion.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        10. Changes to Terms
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        We may update these Terms of Service at any time. Changes will be reflected in the "last updated" date at the top of this page. Your continued use of the Service after changes are posted constitutes acceptance of the updated terms. We encourage you to review these terms periodically.
      </p>

      <h2 style={{ fontFamily: 'Barlow, sans-serif', fontWeight: 800 }} className="text-lg text-slate-200 mt-8 mb-3">
        11. Contact
      </h2>
      <p className="font-mono text-sm text-slate-400 leading-relaxed mb-4">
        If you have questions about these Terms of Service, contact us at{' '}
        <a href="mailto:support@gcodr.dev" className="text-cyan-500/70 hover:text-cyan-400 transition-colors">
          support@gcodr.dev
        </a>.
      </p>
    </LegalPage>
  )
}
